import React from 'react';
import clsx from 'clsx';
import { readStates, saveStates, states } from '@/constants/lifecycle';
import EntitiesErrorMessages from '@/components/errors/EntitiesErrorMessages';
import PanelContainer from '@/components/wrappers/PanelContainer';
import fetch from '../hooks/fetch';
import datasetStatusInitialState from '../status/state/state';
import attachmentsInitialState from '../attachments/state/state';
import { fetchDataset, newDatasetFromSource, updateDatasetProperties } from '../actions';
import { datasetPermissions } from '../sharing/constants';
import { datasetPerms } from '../sharing/state/state';
import canManageDataset from '../sharing/helper';
import { resolveCatalogueTemplate } from '../catalogue/helper';
import { datasetWizardChecks } from '../helper';
import DatasetWizard from '../containers/DatasetWizard';
import { datasetDirtyFlags, datasetIsDirty, datasetSourceAndStepAttributes } from '../state/state';
import { datasetSettingsFields } from '../settings/state/state';
import DatasourceFeaturesFetch from '../datasource/containers/DatasourceFeaturesFetch';

const DatasetPanelContainer = ({
  canCreate,
  canRead,
  category,
  code,
  children,
  className,
  compact,
  datasetUrl,
  exists,
  fetchData,
  isDirty,
  isNew,
  wizardOn,
  name,
  newDataset,
  onChange,
  readErrors,
  readStatus,
  saveErrors,
  templateReadStatus,
  updateProperties,
  url,
}) => {
  fetch.useFetchOrNewDataset(fetchData, newDataset, category, code, readStatus, isNew, datasetUrl);
  return (
    <PanelContainer
      canAccess={isNew ? canCreate : canRead}
      entity="dataset"
      exists={exists}
      isDirty={datasetIsDirty({ isDirty })}
      isNew={isNew}
      name={name}
      onChange={onChange}
      readErrors={wizardOn ? [] : readErrors}
      readStatus={[readStatus, templateReadStatus]}
      url={url}
    >
      <div className={clsx(className, wizardOn && 'panel-wizard')}>
        {wizardOn && (<DatasetWizard category={category} code={code} compact={compact} url={url} />)}
        {!wizardOn && (
          <>
            <EntitiesErrorMessages
              category={category}
              code={code}
              contexts={['dictionaryUUID', 'fieldUUID']}
              entities={['dataset', 'server']}
              entity="dataset"
              errors={saveErrors}
              onDismiss={(errors) => updateProperties(category, code, { saveErrors: errors })}
            />
            {(!isNew && readStatus === states.SUCCESS) && (
              <DatasourceFeaturesFetch category={category} code={code} />
            )}
            {children}
          </>
        )}
      </div>
    </PanelContainer>
  );
};

export const mapStateToProps = ({
  datasets: { datasets, wizardOff },
  catalogues: { templates },
  auth: { role, currentUser },
  datasetDatasource: { features },
}, ownProps) => {
  const { permissions } = role;
  const {
    dataset, template, exists,
  } = resolveCatalogueTemplate(datasets, templates, ownProps, {
    asViewer: { on: false },
    attachments: attachmentsInitialState(),
    catalogue: { title: '' },
    code: ownProps.code,
    isDirty: datasetDirtyFlags(),
    permissions: datasetPerms(),
    ...readStates,
    ...saveStates,
    settings: datasetSettingsFields(),
    status: datasetStatusInitialState(),
    steps: datasetSourceAndStepAttributes(),
  }, features);
  const checks = datasetWizardChecks(dataset, exists, wizardOff, role, ownProps);

  return ({
    ...checks,
    allowManage: permissions.datasets.update && canManageDataset(dataset.permissions),
    asViewer: dataset.asViewer.on,
    canCreate: permissions.datasets.create,
    canEdit: permissions.datasets.update && dataset.permissions[datasetPermissions.UPDATE],
    canRead: exists && permissions.datasets.read && permissions.cataloguedefinitions.read,
    canSubscribe: !currentUser.isGuest && dataset.settings.allow_dataset_update_subscriptions,
    code: dataset.code,
    isDirty: dataset.isDirty,
    name: dataset.catalogue.title,
    readErrors: dataset.readErrors.concat(template.readErrors),
    readStatus: dataset.readStatus,
    saveErrors: dataset.saveErrors,
    templateReadStatus: template.readStatus,
    templateCode: dataset.settings.catalogue_definition_code,
    visibility: dataset.settings.visibility,
  });
};

export const mapDispatchToProps = {
  fetchData: fetchDataset,
  newDataset: newDatasetFromSource,
  updateProperties: updateDatasetProperties,
};

export default DatasetPanelContainer;
